// MyApplicationsList component for Influencer Dashboard
// Shows a clean list of recently applied-to campaigns with expandable rows

import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext.jsx';
import { collection, query, where, orderBy, limit, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebaseConfig.js';
import { deliverableLabels } from '../lib/deliverables.js';
import { 
  APPLICATION_STATUSES,
  normalizeApplicationStatus,
  getStatusBadgeConfig 
} from '../lib/applicationStatus.js';

// Helper component for meta information rows
function MetaRow({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className="text-sm">
      <div className="text-gray-500 mb-1">{label}</div>
      <div className="font-medium text-gray-900 break-words">{value}</div>
    </div>
  );
}

// Helper component for chips (deliverables, requirements)
function Chips({ items, title }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="text-sm text-gray-500 mb-2 font-medium">{title}</div>
      <div className="flex flex-wrap gap-2">
        {deliverableLabels(items).map((label, i) => (
          <span 
            key={i} 
            className="inline-flex items-center rounded-full border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-gray-700"
          >
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}

const MyApplicationsList = () => {

  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchApplications = async () => {
      try {
        if (!user || !db) {
          setRows([]);
          setLoading(false);
          return;
        }

        // Query applications for this influencer
        // Try with orderBy first, fallback if index missing
        let apps = [];
        try {
          const q = query(
            collection(db, 'applications'),
            where('influencerId', '==', user.uid),
            orderBy('submittedAt', 'desc'),
            limit(50)
          );
          const snap = await getDocs(q);
          apps = snap.docs.map(d => ({
            id: d.id,
            ...d.data(),
            submittedAt: d.data().submittedAt,
            appliedAt: d.data().submittedAt || d.data().createdAt // Use submittedAt as appliedAt
          }));
        } catch (orderByError) {
          // Fallback: query without orderBy, sort client-side
          console.warn('OrderBy failed, using fallback:', orderByError);
          const q = query(
            collection(db, 'applications'),
            where('influencerId', '==', user.uid),
            limit(50)
          );
          const snap = await getDocs(q);
          apps = snap.docs.map(d => ({
            id: d.id,
            ...d.data(),
            submittedAt: d.data().submittedAt,
            appliedAt: d.data().submittedAt || d.data().createdAt
          }));
          // Sort client-side by submittedAt descending
          apps.sort((a, b) => {
            const aDate = a.submittedAt?.toDate?.() || a.createdAt || new Date(0);
            const bDate = b.submittedAt?.toDate?.() || b.createdAt || new Date(0);
            return bDate - aDate;
          });
        }

        // Fetch campaigns in parallel
        const campaignDocs = await Promise.all(
          apps.map(app => {
            if (!app.campaignId) return null;
            return getDoc(doc(db, 'campaigns', app.campaignId)).catch(() => null);
          })
        );

        // Build rows with campaign data
        const rows = apps.map((app, i) => {
          const campaignDoc = campaignDocs[i];
          let campaign = null;
          
          if (campaignDoc?.exists()) {
            const campaignData = campaignDoc.data();
            // Fetch brand logo if we have brandId
            campaign = {
              id: campaignDoc.id,
              name: campaignData.title || campaignData.name || campaignData.campaignName || 'Untitled Campaign',
              brandName: campaignData.brandName || app.brandName || null,
              brandId: campaignData.brandId || app.brandId || null,
              payout: campaignData.budgetUSD || campaignData.budget || campaignData.payment_cents ? 
                (campaignData.budgetUSD || (campaignData.payment_cents / 100) || campaignData.budget) : null,
              deliverables: campaignData.deliverables || null
            };
          } else {
            // Campaign not found, use data from application
            campaign = {
              id: app.campaignId,
              name: app.campaignTitle || app.campaignName || 'Campaign unavailable',
              brandName: app.brandName || null,
              brandId: app.brandId || null,
              payout: app.snapshot?.budgetUSD || null,
              deliverables: null
            };
          }

          return {
            application: {
              id: app.id,
              campaignId: app.campaignId,
              influencerId: app.influencerId,
              status: normalizeApplicationStatus(app.status || APPLICATION_STATUSES.PENDING_REVIEW),
              appliedAt: app.appliedAt || app.submittedAt || app.createdAt
            },
            campaign
          };
        });

        // Fetch brand logos for campaigns that have brandId
        const uniqueBrandIds = [...new Set(rows.map(r => r.campaign?.brandId).filter(Boolean))];
        const brandLogos = {};
        
        await Promise.all(
          uniqueBrandIds.map(async (brandId) => {
            try {
              const brandRef = doc(db, 'users', brandId);
              const brandSnap = await getDoc(brandRef);
              if (brandSnap.exists()) {
                brandLogos[brandId] = brandSnap.data().logoUrl || null;
              }
            } catch (err) {
              console.warn(`Could not fetch brand logo for ${brandId}:`, err);
            }
          })
        );

        // Add brand logos to campaigns
        rows.forEach(row => {
          if (row.campaign?.brandId && brandLogos[row.campaign.brandId]) {
            row.campaign.brandLogoUrl = brandLogos[row.campaign.brandId];
          }
        });

        setRows(rows);
      } catch (e) {
        console.error('Error fetching applications:', e);
        setError('Failed to load applications.');
      } finally {
        setLoading(false);
      }
    };

    fetchApplications();
  }, [user]);

  // Format date helper
  const formatDate = (timestamp) => {
    if (!timestamp) return '—';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }).format(date);
    } catch (e) {
      return '—';
    }
  };

  // Format payout helper
  const formatPayout = (payout) => {
    if (!payout && payout !== 0) return null;
    if (typeof payout === 'number') {
      return `$${payout.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    }
    return payout;
  };

  // Status badge helper
  const getStatusBadge = (status) => {
    const normalized = normalizeApplicationStatus(status);
    const config = getStatusBadgeConfig(normalized);
    
    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${config.className}`}>
        {config.icon} {config.label}
      </span>
    );
  };

  // Get initials for brand logo fallback
  const getInitials = (name) => {
    if (!name) return 'BR';
    const words = name.trim().split(/\s+/);
    if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  };

  // Lazy-load full campaign details on first expand
  const fetchCampaignFull = useCallback(async (campaignId) => {
    try {
      if (!db || !campaignId) return null;
      const campaignRef = doc(db, 'campaigns', campaignId);
      const campaignSnap = await getDoc(campaignRef);
      if (!campaignSnap.exists()) return null;
      
      const data = campaignSnap.data();
      return {
        id: campaignSnap.id,
        name: data.title || data.name || data.campaignName || 'Untitled Campaign',
        brandName: data.brandName || null,
        brandLogoUrl: null, // Will be set from existing row data
        description: data.bio || data.description || data.brief || null,
        deliverables: data.deliverables || null,
        requirements: data.requirements || null,
        payout: data.budgetUSD || data.budget || (data.payment_cents ? data.payment_cents / 100 : null),
        budgetMin: data.budgetMin || null,
        budgetMax: data.budgetMax || null,
        deadline: data.deadline || null,
        productLink: data.productLink || null,
        brandWebsite: data.brandWebsite || data.website || null,
        collaborationType: data.collaborationType || null,
        shipFrom: data.shippingFromCountry || data.shippingFrom || data.shipFrom || null,
      };
    } catch (err) {
      console.error('Error fetching full campaign:', err);
      return null;
    }
  }, []);

  // ExpandableRow component
  function ExpandableRow({ row, fetchCampaignFull, getInitials, formatDate, formatPayout, getStatusBadge }) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [full, setFull] = useState(null);

    const onToggle = useCallback(async () => {
      if (!open && !full) {
        setLoading(true);
        const data = await fetchCampaignFull(row.application.campaignId);
        if (data) {
          // Preserve brand logo from initial load
          data.brandLogoUrl = row.campaign?.brandLogoUrl || null;
        }
        setFull(data);
        setLoading(false);
      }
      setOpen(v => !v);
    }, [open, full, row.application.campaignId, row.campaign?.brandLogoUrl, fetchCampaignFull]);

    const fmtDate = (timestamp) => {
      if (!timestamp) return '—';
      try {
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return new Intl.DateTimeFormat('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        }).format(date);
      } catch (e) {
        return '—';
      }
    };

    const formatBudgetRange = () => {
      if (full?.budgetMin != null || full?.budgetMax != null) {
        const min = full.budgetMin != null ? `$${full.budgetMin.toLocaleString()}` : '—';
        const max = full.budgetMax != null ? `$${full.budgetMax.toLocaleString()}` : '—';
        return `${min}–${max}`;
      }
      return null;
    };

    return (
      <div className="rounded-2xl border border-gray-200 p-4 shadow-sm bg-white hover:shadow-md transition-shadow">
        {/* Row header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            {/* Brand Logo */}
            <div className="h-10 w-10 rounded-xl bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0 relative">
              {row.campaign?.brandLogoUrl ? (
                <img 
                  src={row.campaign.brandLogoUrl} 
                  alt={row.campaign.brandName || 'Brand'} 
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    if (e.target) {
                      e.target.style.display = 'none';
                      const fallback = e.target.parentElement?.querySelector('.logo-fallback');
                      if (fallback) fallback.style.display = 'flex';
                    }
                  }}
                />
              ) : null}
              <div className={`logo-fallback absolute inset-0 flex items-center justify-center ${row.campaign?.brandLogoUrl ? 'hidden' : ''}`}>
                <span className="text-xs font-semibold text-gray-600">
                  {getInitials(row.campaign?.brandName)}
                </span>
              </div>
            </div>

            {/* Campaign Info */}
            <div className="flex-1 min-w-0">
              <div className="text-sm text-gray-500 truncate">
                {row.campaign?.brandName || 'Unknown brand'}
              </div>
              <div className="text-base font-semibold text-gray-900 truncate">
                {row.campaign?.name || 'Campaign unavailable'}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                Applied {formatDate(row.application.appliedAt)}
                {row.campaign?.deliverables && Array.isArray(row.campaign.deliverables) && row.campaign.deliverables.length > 0 && (
                  <> · {deliverableLabels(row.campaign.deliverables).join(', ')}</>
                )}
                {(!row.campaign?.deliverables || !Array.isArray(row.campaign.deliverables) || row.campaign.deliverables.length === 0) && (
                  <> · Deliverables TBD</>
                )}
              </div>
            </div>
          </div>

          {/* Right side: Payout, Status, View button */}
          <div className="flex items-center gap-4 flex-shrink-0">
            {row.campaign?.payout && (
              <div className="text-sm text-gray-600 whitespace-nowrap">
                Payout: {formatPayout(row.campaign.payout)}
              </div>
            )}
            <div className="flex flex-col items-end gap-1">
              {getStatusBadge(row.application.status)}
              {normalizeApplicationStatus(row.application.status) === APPLICATION_STATUSES.ACCEPTED && (
                <p className="text-xs text-slate-500 mt-1 whitespace-nowrap">
                  You've been accepted! The brand will contact you via email with next steps. Check your inbox.
                </p>
              )}
            </div>
            <button
              onClick={onToggle}
              aria-expanded={open}
              aria-controls={`panel-${row.application.id}`}
              className="rounded-xl border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
            >
              {open ? 'Hide' : 'View'}
            </button>
          </div>
        </div>

        {/* Dropdown panel */}
        <div
          id={`panel-${row.application.id}`}
          className={`transition-all duration-200 ease-out overflow-hidden ${
            open 
              ? 'max-h-[1000px] opacity-100 scale-100 mt-4' 
              : 'max-h-0 opacity-0 scale-95'
          }`}
        >
          {loading ? (
            <div className="space-y-3 pt-4">
              <div className="h-4 w-3/4 bg-gray-100 rounded animate-pulse" />
              <div className="h-4 w-2/3 bg-gray-100 rounded animate-pulse" />
              <div className="h-32 w-full bg-gray-100 rounded animate-pulse" />
            </div>
          ) : full ? (
            <div className="space-y-5 pt-4 border-t border-gray-200">
              {/* Description/Brief */}
              {full.description && (
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-2">Campaign Brief</div>
                  <div className="text-sm text-gray-800 whitespace-pre-line leading-relaxed">
                    {full.description}
                  </div>
                </div>
              )}

              {/* Deliverables */}
              <Chips items={full.deliverables} title="Deliverables" />

              {/* Requirements */}
              <Chips items={full.requirements} title="Requirements" />

              {/* Meta grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <MetaRow label="Collaboration Type" value={full.collaborationType} />
                <MetaRow label="Deadline" value={fmtDate(full.deadline)} />
                <MetaRow label="Payout" value={full.payout ? formatPayout(full.payout) : null} />
                <MetaRow label="Budget Range" value={formatBudgetRange()} />
                <MetaRow label="Ships From" value={full.shipFrom} />
                {full.productLink && (
                  <div className="text-sm">
                    <div className="text-gray-500 mb-1">Product Link</div>
                    <div className="font-medium text-gray-900 break-words">
                      <a 
                        href={full.productLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {full.productLink}
                      </a>
                    </div>
                  </div>
                )}
                {full.brandWebsite && (
                  <div className="text-sm">
                    <div className="text-gray-500 mb-1">Brand Website</div>
                    <div className="font-medium text-gray-900 break-words">
                      <a 
                        href={full.brandWebsite} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {full.brandWebsite}
                      </a>
                    </div>
                  </div>
                )}
              </div>

              {/* Accepted status helper text */}
              {normalizeApplicationStatus(row.application.status) === APPLICATION_STATUSES.ACCEPTED && (
                <div className="bg-slate-50 border border-slate-200 p-3 rounded-lg mt-4">
                  <p className="text-sm text-slate-600">
                    <strong>You've been accepted!</strong> The brand will contact you via email with next steps. Check your inbox for collaboration details.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-500">Unable to load campaign details.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-3">
        <div className="h-16 w-full rounded-2xl bg-gray-100 animate-pulse" />
        <div className="h-16 w-full rounded-2xl bg-gray-100 animate-pulse" />
        <div className="h-16 w-full rounded-2xl bg-gray-100 animate-pulse" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  if (!rows || rows.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-lg font-medium text-gray-900">No applications yet</p>
        <p className="text-sm text-gray-500 mt-1">When you apply to campaigns, they'll appear here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {rows.map((row) => (
        <ExpandableRow
          key={row.application.id}
          row={row}
          fetchCampaignFull={fetchCampaignFull}
          getInitials={getInitials}
          formatDate={formatDate}
          formatPayout={formatPayout}
          getStatusBadge={getStatusBadge}
        />
      ))}
    </div>
  );
};

export default MyApplicationsList;

